import type { LucideIcon } from "lucide-react";
import {
  AlertCircle,
  AlertTriangle,
  Archive,
  Ban,
  Banknote,
  Coins,
  CalendarClock,
  CheckCircle2,
  CircleDashed,
  CircleDot,
  CircleSlash,
  Clock,
  Copy,
  Info,
  Loader2,
  Lock,
  MinusCircle,
  PauseCircle,
  PencilLine,
  RotateCcw,
  ScanSearch,
  Send,
  X,
} from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "./button";
import { cn } from "./cn";

/* -------------------------------------------------------------------------- */
/*  Status                                                                     */
/* -------------------------------------------------------------------------- */

export type StatusTone = "neutral" | "success" | "warning" | "danger" | "accent";

const TONE: Record<StatusTone, string> = {
  neutral: "bg-surface-sunken text-muted border-line",
  success: "bg-success-soft text-success-on-soft border-success-soft-border",
  warning: "bg-warning-soft text-warning-on-soft border-warning-soft-border",
  danger: "bg-danger-soft text-danger-on-soft border-danger-soft-border",
  accent: "bg-accent-soft text-accent-on-soft border-accent-soft-border",
};

/**
 * Every status carries an icon as well as a colour.
 *
 * This is what fixed the worst legibility bug in the old UI: `Overdue` and
 * `Changes requested` were both rose, separated only by a background alpha of
 * /40 versus /60. Nobody could tell them apart at a glance, and to a red-green
 * colourblind user they were the same chip. They now differ by hue *and* glyph —
 * "returned to you" (amber, a rewind) is a different kind of event from "you are
 * late" (rose, a warning triangle), and either signal alone is enough to tell
 * them apart.
 *
 * Keyed by lowercased label rather than by the label itself: the old map was
 * keyed by exact display string, so renaming a label anywhere silently dropped
 * the badge to grey with no error.
 */
const STATUS_META: Record<string, { tone: StatusTone; icon: LucideIcon }> = {
  approved: { tone: "success", icon: CheckCircle2 },
  active: { tone: "success", icon: CheckCircle2 },
  submitted: { tone: "accent", icon: Clock },
  pending: { tone: "warning", icon: Clock },
  due: { tone: "warning", icon: Clock },
  "changes requested": { tone: "warning", icon: RotateCcw },
  overdue: { tone: "danger", icon: AlertTriangle },
  revoked: { tone: "danger", icon: Ban },
  draft: { tone: "neutral", icon: PencilLine },
  locked: { tone: "neutral", icon: Lock },
  inactive: { tone: "neutral", icon: MinusCircle },
  "not engaged": { tone: "neutral", icon: MinusCircle },
  upcoming: { tone: "neutral", icon: CircleDashed },
  empty: { tone: "neutral", icon: CircleDashed },
  // The admin lists. These fell through to the grey fallback, so on the pages
  // whose whole job is to tell these states apart, "Invited" was pixel-identical
  // to "Inactive", and "Archived" to "Ended".
  //
  // Only `invited` takes a colour: an invite is in flight and awaiting the
  // invitee, which is a live state, and its glyph matches the Send icon on the
  // "Resend invite" action beside it. The retired states stay deliberately muted
  // — a closed project is not a warning — and separate by glyph instead.
  invited: { tone: "accent", icon: Send },
  archived: { tone: "neutral", icon: Archive },
  closed: { tone: "neutral", icon: Archive },
  "on hold": { tone: "neutral", icon: PauseCircle },
  ended: { tone: "neutral", icon: CircleSlash },
  current: { tone: "accent", icon: CircleDot },
  // Contractor invoices (CINV): the workflow labels …
  "awaiting review": { tone: "accent", icon: Clock },
  // Expense invoices (EXP): recorded is waiting on a decision, void is the delete.
  recorded: { tone: "accent", icon: Clock },
  void: { tone: "neutral", icon: Ban },
  "correction requested": { tone: "warning", icon: RotateCcw },
  "approved for payment": { tone: "success", icon: CheckCircle2 },
  // Some of it paid, some still owed (CINV-23): not done, so not green.
  "part paid": { tone: "warning", icon: Coins },
  paid: { tone: "success", icon: Banknote },
  rejected: { tone: "danger", icon: Ban },
  // … and the engine's verdicts. Duplicate has no colour of its own in the
  // palette, so it borrows accent and separates from Mismatch/Needs review by
  // glyph, which is the rule this whole map exists for.
  matched: { tone: "success", icon: CheckCircle2 },
  mismatch: { tone: "danger", icon: AlertTriangle },
  "needs review": { tone: "warning", icon: AlertCircle },
  duplicate: { tone: "accent", icon: Copy },
  "missing timesheet": { tone: "danger", icon: CalendarClock },
  "extraction failed": { tone: "neutral", icon: ScanSearch },
  extracting: { tone: "accent", icon: Loader2 },
};

export function statusTone(status: string): StatusTone {
  return STATUS_META[status.toLowerCase()]?.tone ?? "neutral";
}

export function StatusBadge({
  status,
  tone,
  icon,
  className = "",
}: {
  status: string;
  /**
   * Overrides for a chip whose text is not a status — the AI-confidence pill,
   * say — so it can borrow this chrome without an entry in the map.
   */
  tone?: StatusTone;
  icon?: LucideIcon;
  className?: string;
}) {
  const meta = STATUS_META[status.toLowerCase()];
  const Icon = icon ?? meta?.icon ?? CircleDashed;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-micro font-medium",
        TONE[tone ?? meta?.tone ?? "neutral"],
        className
      )}
    >
      <Icon
        className={cn("h-3 w-3 shrink-0", Icon === Loader2 && "animate-spin")}
        aria-hidden="true"
      />
      {status}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*  Alert                                                                      */
/* -------------------------------------------------------------------------- */

const ALERT_TONE = {
  ok: {
    box: "bg-success-soft text-success-on-soft border-success-soft-border",
    icon: CheckCircle2,
  },
  error: {
    box: "bg-danger-soft text-danger-on-soft border-danger-soft-border",
    icon: AlertCircle,
  },
  warn: {
    box: "bg-warning-soft text-warning-on-soft border-warning-soft-border",
    icon: AlertTriangle,
  },
  info: {
    box: "bg-accent-soft text-accent-on-soft border-accent-soft-border",
    icon: Info,
  },
} as const;

export function Alert({
  tone,
  title,
  children,
  action,
  onDismiss,
  className = "",
}: {
  tone: "ok" | "error" | "warn" | "info";
  title?: ReactNode;
  children?: ReactNode;
  /** Rendered at the trailing edge — a "Retry" or "Reload" affordance. */
  action?: ReactNode;
  /**
   * Makes the alert closeable. Pass it only for a message about something that
   * has **already happened** — "submitted for approval", "returned to draft" —
   * which the reader can be finished with.
   *
   * Not for one describing a live condition. The cap and note alerts on the
   * timesheet are the reason a button is disabled; dismissing one would leave a
   * contractor staring at a Submit they cannot press with the explanation gone.
   * Those pass no `onDismiss` and cannot be closed, which is correct.
   *
   * The close button does not touch `role`: `tone="error"` announces itself
   * through `role="alert"` and callers depend on that as their live region.
   */
  onDismiss?: () => void;
  className?: string;
}) {
  const { box, icon: Icon } = ALERT_TONE[tone];
  return (
    <div
      className={cn(
        "flex items-start gap-2.5 rounded-lg border px-4 py-3 text-sm",
        box,
        className
      )}
      role={tone === "error" ? "alert" : tone === "info" ? "status" : undefined}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        {title && <div className="font-semibold">{title}</div>}
        {children && <div className={cn(title ? "mt-0.5" : null)}>{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
      {onDismiss && (
        // The kit's own close button, as Modal and Drawer use it — so it inherits
        // the focus ring and hit target rather than being a bare <button> that
        // looks the same until someone tabs to it.
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          onClick={onDismiss}
          aria-label="Dismiss"
          className="-mr-1.5 -mt-0.5 shrink-0"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Loading                                                                    */
/* -------------------------------------------------------------------------- */

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-sm text-subtle">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      {label ?? "Loading…"}
    </div>
  );
}

/**
 * A shaped placeholder.
 *
 * Preferred over `Spinner` wherever the shape of the incoming content is known:
 * a skeleton holds the layout still, where a centred spinner throws the page away
 * and then snaps it back. `aria-hidden` because the loading state is announced
 * once by the region that owns it, not once per grey box.
 */
export function Skeleton({ className = "" }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn("animate-pulse-soft rounded-md bg-surface-sunken", className)}
    />
  );
}

export function SkeletonText({
  lines = 3,
  className = "",
}: {
  lines?: number;
  className?: string;
}) {
  return (
    <div className={cn("space-y-2", className)}>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} className={cn("h-3.5", i === lines - 1 ? "w-2/3" : "w-full")} />
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Empty state                                                                */
/* -------------------------------------------------------------------------- */

/**
 * There were three different empty-state treatments and one page with none at
 * all. This is the one.
 *
 * `tone="positive"` matters more than it looks: "Everyone is up to date" on the
 * chase list is *good news*, and rendering it in the same flat grey as "no
 * results, your filter is too narrow" tells the reader nothing about which one
 * they are looking at.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  tone = "neutral",
  className = "",
}: {
  icon?: LucideIcon;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  tone?: "neutral" | "positive";
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center px-6 py-12 text-center",
        className
      )}
    >
      {Icon && (
        <div
          className={cn(
            "mb-3 grid h-11 w-11 place-items-center rounded-xl border",
            tone === "positive"
              ? "border-success-soft-border bg-success-soft text-success-on-soft"
              : "border-line bg-surface-sunken text-subtle"
          )}
        >
          <Icon className="h-5 w-5" aria-hidden="true" />
        </div>
      )}
      <p className="text-md font-semibold text-text">{title}</p>
      {description && <p className="mt-1 max-w-sm text-sm text-subtle">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
